import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFeatureEnabled } from '@/lib/entitlements';
import { filterNavByFeature, navEntries, type NavGroup, type NavLeaf } from './nav-config';

/**
 * Primary navigation. Matches the RxBB Figma "Home" sidebar (node 11050:1534):
 * 280px wide, translucent dark glass (rgba(11,18,32,0.68) + blur), right hairline
 * border. Flat items link directly; "Orbis" groups expand into a panel of
 * sub-pages. Active items are brand-orange.
 */
interface SidebarProps {
  /**
   * When true the sidebar collapses to zero width. The transition animates the
   * `width` and the `border-r` fades so the collapse reads as the shell reflowing
   * rather than a hard swap. Toggled by the hamburger in `Topbar`.
   */
  collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const isEnabled = useFeatureEnabled();
  const entries = filterNavByFeature(navEntries, isEnabled);

  return (
    // Scrolls independently of the content pane: the shell is a fixed viewport
    // height, and expanding several nav groups can outgrow it.
    <aside
      className={cn(
        'hidden shrink-0 flex-col overflow-y-auto overscroll-contain bg-[rgba(11,18,32,0.68)] backdrop-blur-md transition-[width,border-color] duration-200 ease-out lg:flex',
        collapsed ? 'w-0 border-r-0 border-r-transparent' : 'w-[280px] border-r border-[#1F2937]',
      )}
      aria-hidden={collapsed}
    >
      <nav className="flex w-[280px] flex-col gap-3 px-2.5 pb-4 pt-6">
        {entries.map((entry) =>
          entry.type === 'item' ? (
            <NavItemLink key={entry.to} to={entry.to} label={entry.label} Icon={entry.icon} />
          ) : (
            <NavGroupBlock key={entry.label} group={entry} />
          ),
        )}
      </nav>
    </aside>
  );
}

const baseRow = 'flex items-center gap-3 rounded-xl px-4 py-3 text-[16px] transition-colors';

function NavItemLink({
  to,
  label,
  Icon,
}: {
  to: string;
  label: NavLeaf['label'];
  Icon: NonNullable<NavLeaf['icon']>;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          baseRow,
          isActive
            ? 'bg-brand-orange font-medium text-white'
            : 'text-white/85 hover:bg-white/5 hover:text-white',
        )
      }
    >
      <Icon size={22} strokeWidth={1.75} className="shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function NavGroupBlock({ group }: { group: NavGroup }) {
  const location = useLocation();
  const containsActive = group.children.some(
    (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'),
  );
  const [open, setOpen] = useState(containsActive);
  const GroupIcon = group.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          baseRow,
          'w-full justify-between text-white/85 hover:bg-white/5 hover:text-white',
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <GroupIcon size={22} strokeWidth={1.75} className="shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
        </span>
        <ChevronDown
          size={18}
          className={cn('ml-2 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="mt-1 flex flex-col gap-1 rounded-xl bg-[#1F2937]/60 p-1.5">
          {group.children.map((child) => {
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.to}
                to={child.to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-4 py-2.5 text-[15px] transition-colors',
                    isActive
                      ? 'bg-brand-orange font-medium text-white'
                      : 'text-white/75 hover:bg-white/5 hover:text-white',
                  )
                }
              >
                {ChildIcon && <ChildIcon size={20} strokeWidth={1.75} className="shrink-0" />}
                <span className="truncate">{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
